import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  User,
  Mail,
  Calendar,
  MessageSquare,
  Users,
  CreditCard,
  HeadphonesIcon,
  AlertTriangle,
  Clock3,
  Trash2
} from 'lucide-react';

const getElapsedDetailedLabel = (dateStr) => {
  if (!dateStr) return '-';
  const start = new Date(dateStr);
  if (Number.isNaN(start.getTime())) return '-';
  const now = new Date();
  const diffMs = Math.max(now - start, 0);
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const months = Math.floor(totalDays / 30);
  const days = totalDays % 30;

  if (months > 0) {
    return `${months} mes(es) e ${days} dia(s)`;
  }
  return `${totalDays} dia(s)`;
};

const getPlanDisplayName = (plan) => {
  if (plan === 'starter') return 'Starter';
  if (plan === 'pro') return 'Pro';
  if (plan === 'business') return 'Premium';
  return 'Plano';
};

const getBillingCycleLabel = (cycle) => {
  if (cycle === 'monthly') return 'Mensal';
  if (cycle === 'annual') return 'Anual';
  if (cycle === 'free') return 'Sem assinatura';
  return 'Teste';
};

const formatCurrency = (value) => `R$ ${Number(value || 0).toFixed(2).replace('.', ',')}`;

const formatDateTimeLabel = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-BR');
};

const getFinancialStatusBadge = (user) => {
  if (!user) {
    return { label: '-', className: 'bg-zinc-100 text-zinc-700' };
  }

  if (user.plan_status === 'trialing' || user.billing_status === 'trialing') {
    return { label: 'Em teste', className: 'bg-sky-100 text-sky-700' };
  }

  if (user.billing_status === 'overdue') {
    return { label: `${user.days_overdue || 0} dia(s) em atraso`, className: 'bg-red-100 text-red-700' };
  }

  const nextBillingDate = user.next_billing_date ? new Date(user.next_billing_date) : null;
  if (!nextBillingDate || Number.isNaN(nextBillingDate.getTime())) {
    return { label: 'Em dia', className: 'bg-emerald-100 text-emerald-700' };
  }

  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilDue = Math.ceil((nextBillingDate.setHours(0, 0, 0, 0) - new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()) / msPerDay);

  if (daysUntilDue <= 0) {
    return { label: 'Vence hoje', className: 'bg-amber-100 text-amber-700' };
  }
  if (daysUntilDue <= 5) {
    return { label: `Próximo vencimento (${daysUntilDue} dia${daysUntilDue === 1 ? '' : 's'})`, className: 'bg-amber-100 text-amber-700' };
  }
  return { label: 'Em dia', className: 'bg-emerald-100 text-emerald-700' };
};

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: adminUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);

  const fetchUserData = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        setUserData(await response.json());
      } else {
        toast.error('Usuário não encontrado');
        navigate('/admin');
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    if (adminUser?.role !== 'admin') {
      navigate('/app', { replace: true });
      return;
    }
    fetchUserData();
  }, [adminUser, navigate, fetchUserData]);

  const updateUser = async (field, value) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });

      if (response.ok) {
        toast.success('Usuário atualizado');
        fetchUserData();
      } else {
        toast.error('Erro ao atualizar');
      }
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDeleteUser = async () => {
    setDeletingUser(true);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirmation_phrase: deleteConfirmation }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Não foi possível excluir o usuário.');
      }

      toast.success(data.message || 'Usuário excluído com sucesso.');
      navigate('/admin', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Erro ao excluir usuário');
    } finally {
      setDeletingUser(false);
    }
  };

  const getPlanBadge = (plan) => {
    const styles = {
      starter: 'bg-zinc-100 text-zinc-700',
      pro: 'bg-violet-100 text-violet-700',
      business: 'bg-amber-100 text-amber-700'
    };
    return <Badge className={`${styles[plan]} border-0`}>{plan}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { user, workspace, activity, tickets } = userData || {};
  const accountSegment = user?.account_segment === 'business' ? 'business' : 'personal';
  const financialStatus = getFinancialStatusBadge(user);
  const canDeleteUser = user?.role !== 'admin' && user?.user_id !== adminUser?.user_id;
  const deletePhraseIsValid = deleteConfirmation.trim().toUpperCase() === 'EXCLUIR USUARIO';

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="font-heading text-lg font-semibold">Detalhes do Usuário</span>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {/* User Info */}
        <Card className="border-zinc-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-violet-100 text-violet-600 text-2xl">
                  {user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-bold text-zinc-900">{user?.name}</h1>
                  {user?.role === 'admin' && (
                    <Badge className="bg-red-100 text-red-700 border-0">Admin</Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-6 text-sm text-zinc-500">
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {user?.email}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Desde {user?.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '-'}
                  </div>
                  <div>
                    {getElapsedDetailedLabel(user?.created_at)} na plataforma
                  </div>
                </div>

                <div className="font-mono text-xs text-zinc-400 mt-2">
                  ID: {user?.user_id}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Plan & Status */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-violet-600" />
                Plano
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-500 mb-2 block">Plano Atual</label>
                  <Select 
                    value={user?.plan || 'starter'} 
                    onValueChange={(v) => updateUser('plan', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="business">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-zinc-500 mb-2 block">Segmento da conta</label>
                  <Select
                    value={accountSegment}
                    onValueChange={(v) => updateUser('account_segment', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="personal">Pessoal</SelectItem>
                      <SelectItem value="business">Business</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {user?.plan !== 'starter' && (
                  <div>
                    <label className="text-sm text-zinc-500 mb-2 block">Cobrança</label>
                    <Select
                      value={user?.billing_cycle || 'annual'}
                      onValueChange={(v) => updateUser('billing_cycle', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">
                          Mensal {user?.plan === 'business'
                            ? accountSegment === 'business' ? '(R$ 69,90)' : '(R$ 39,97)'
                            : accountSegment === 'business' ? '(R$ 19,90)' : '(R$ 9,97)'}
                        </SelectItem>
                        <SelectItem value="annual">
                          Anual {user?.plan === 'business'
                            ? accountSegment === 'business' ? '(R$ 49,90/mês)' : '(R$ 29,97/mês)'
                            : accountSegment === 'business' ? '(R$ 12,90/mês)' : '(R$ 6,97/mês)'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <label className="text-sm text-zinc-500 mb-2 block">Status</label>
                  <Select 
                    value={user?.plan_status || 'active'} 
                    onValueChange={(v) => updateUser('plan_status', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="trialing">Em teste</SelectItem>
                      <SelectItem value="cancelled">Cancelado</SelectItem>
                      <SelectItem value="expired">Expirado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-3 bg-zinc-50 rounded-lg">
                  <p className="text-sm text-zinc-600">
                    Plano atual: <span className="font-medium">{user?.plan === 'business' && accountSegment === 'business' ? 'Premium' : getPlanDisplayName(user?.plan)} {user?.plan !== 'starter' ? `• ${getBillingCycleLabel(user?.billing_cycle || 'annual')}` : ''}</span>
                  </p>
                  {user?.plan_status === 'trialing' ? (
                    <p className="text-sm text-sky-700 mt-1">
                      Teste ativo até: <span className="font-medium">{formatDateTimeLabel(user?.trial_ends_at || user?.next_billing_date)}</span>
                    </p>
                  ) : null}
                  <p className="text-sm text-zinc-600 mt-1">
                    Tempo no plano atual: <span className="font-medium">{getElapsedDetailedLabel(user?.plan_started_at || user?.created_at)}</span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock3 className="w-5 h-5 text-violet-600" />
                Resumo Financeiro
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-zinc-500">Status atual</p>
                  <p className="text-lg font-semibold text-zinc-900 mt-1">{financialStatus.label}</p>
                </div>
                <Badge className={`${financialStatus.className} border-0`}>
                  {user?.billing_status === 'overdue' ? 'Em atraso' : user?.plan_status === 'trialing' ? 'Teste' : 'Financeiro ok'}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-lg bg-zinc-50 p-4">
                  <p className="text-sm text-zinc-500">Valor cobrado</p>
                  <p className="text-xl font-bold text-zinc-900 mt-1">
                    {formatCurrency(user?.billing_amount)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    {user?.plan_status === 'trialing' ? `Cobrança após 7 dias • ${getBillingCycleLabel(user?.billing_cycle)}` : `${getBillingCycleLabel(user?.billing_cycle)} • ${getPlanDisplayName(user?.plan)}`}
                  </p>
                </div>
                <div className="rounded-lg bg-zinc-50 p-4">
                  <p className="text-sm text-zinc-500">Próxima cobrança</p>
                  <p className="text-sm font-semibold text-zinc-900 mt-1">
                    {user?.plan === 'starter' ? 'Não se aplica' : formatDateTimeLabel(user?.next_billing_date)}
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    Último pagamento: {user?.plan_status === 'trialing' ? 'Ainda não cobrado' : formatDateTimeLabel(user?.last_payment_at)}
                  </p>
                </div>
              </div>

              {user?.plan !== 'starter' && (
                <div className={`rounded-xl border px-4 py-3 ${user?.billing_status === 'overdue' ? 'border-red-200 bg-red-50' : user?.plan_status === 'trialing' ? 'border-sky-200 bg-sky-50' : 'border-emerald-200 bg-emerald-50'}`}>
                  <p className={`text-sm font-medium ${user?.billing_status === 'overdue' ? 'text-red-700' : user?.plan_status === 'trialing' ? 'text-sky-700' : 'text-emerald-700'}`}>
                    {user?.billing_status === 'overdue'
                      ? `Cliente em atraso há ${user?.days_overdue || 0} dia(s)`
                      : user?.plan_status === 'trialing'
                        ? `Teste ativo até ${formatDateTimeLabel(user?.trial_ends_at || user?.next_billing_date)}`
                      : financialStatus.label}
                  </p>
                  <p className={`text-xs mt-1 ${user?.billing_status === 'overdue' ? 'text-red-600' : user?.plan_status === 'trialing' ? 'text-sky-600' : 'text-emerald-600'}`}>
                    {user?.billing_status === 'overdue'
                      ? user?.delinquency_rule?.action || 'Acompanhar cobrança conforme a régua.'
                      : user?.plan_status === 'trialing'
                        ? 'Cartão cadastrado. Se o cliente cancelar antes do fim do teste, não haverá cobrança.'
                      : 'Sem ação de cobrança necessária no momento.'}
                  </p>
                </div>
              )}

              {user?.billing_status === 'overdue' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Régua de cobrança atual: {user?.delinquency_rule?.label || 'Atraso'}
                      </p>
                      <p className="text-xs text-amber-700 mt-1">
                        {user?.delinquency_rule?.action || 'Executar contato e acompanhamento manual.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-1 gap-6">
          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-violet-600" />
                Atividade
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-zinc-900">{activity?.groups_count || 0}</p>
                  <p className="text-sm text-zinc-500">Grupos</p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-zinc-900">{activity?.messages_count || 0}</p>
                  <p className="text-sm text-zinc-500">Mensagens</p>
                </div>
              </div>
              
              {workspace && (
                <div className="mt-4 p-3 bg-zinc-50 rounded-lg">
                  <p className="text-sm text-zinc-600">
                    Workspace: <span className="font-medium">{workspace.name}</span>
                  </p>
                  <p className="text-xs text-zinc-400 mt-1">
                    {workspace.sandbox_mode ? 'Modo Sandbox ativo' : 'Dados importados'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-violet-600" />
              Histórico de Planos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(user?.plan_history || []).length > 0 ? (
                user.plan_history
                  .slice()
                  .reverse()
                  .map((entry, index) => (
                    <div key={`${entry.plan}-${entry.started_at}-${index}`} className="p-4 bg-zinc-50 rounded-lg flex items-start justify-between gap-4">
                      <div>
                        <div className="mb-2">{getPlanBadge(entry.plan)}</div>
                        {entry.plan !== 'starter' && (
                          <p className="text-sm text-zinc-600 mb-2">
                            Cobrança: <span className="font-medium">{getBillingCycleLabel(entry.billing_cycle || 'annual')}</span>
                          </p>
                        )}
                        <p className="text-sm text-zinc-600">
                          Início: <span className="font-medium">{entry.started_at ? new Date(entry.started_at).toLocaleString('pt-BR') : '-'}</span>
                        </p>
                        <p className="text-sm text-zinc-600">
                          {entry.ended_at ? 'Fim' : 'Atual'}: <span className="font-medium">{entry.ended_at ? new Date(entry.ended_at).toLocaleString('pt-BR') : 'Plano vigente'}</span>
                        </p>
                      </div>
                      <div className="text-right text-sm text-zinc-500">
                        <p>{getElapsedDetailedLabel(entry.started_at)}</p>
                        {entry.changed_by && (
                          <p className="text-xs text-zinc-400 mt-1">Alterado por: {entry.changed_by}</p>
                        )}
                      </div>
                    </div>
                  ))
              ) : (
                <p className="text-sm text-zinc-500">Nenhuma troca de plano registrada ainda.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Role Management */}
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-violet-600" />
              Permissões
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <label className="text-sm text-zinc-500 mb-2 block">Função</label>
              <Select 
                value={user?.role || 'user'} 
                onValueChange={(v) => updateUser('role', v)}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuário</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" />
              Ação Crítica
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-700">
              Exclui a conta, workspace, grupos, mensagens, faturas locais, tickets e histórico vinculado ao usuário de teste.
            </p>
            <p className="text-xs text-red-600 mt-2">
              Essa ação é irreversível. Administradores e o próprio usuário admin logado não podem ser excluídos por aqui.
            </p>
            <Button
              className="mt-4 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={!canDeleteUser}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir usuário e histórico
            </Button>
          </CardContent>
        </Card>

        {/* Support Tickets */}
        {tickets && tickets.length > 0 && (
          <Card className="border-zinc-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <HeadphonesIcon className="w-5 h-5 text-violet-600" />
                Tickets de Suporte
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div 
                    key={ticket.ticket_id}
                    className="p-3 bg-zinc-50 rounded-lg flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{ticket.subject}</p>
                      <p className="text-xs text-zinc-500">
                        {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    <Badge className={`${
                      ticket.status === 'open' ? 'bg-orange-100 text-orange-700' :
                      ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-zinc-100 text-zinc-700'
                    } border-0`}>
                      {ticket.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir usuário e histórico</DialogTitle>
            <DialogDescription>
              Essa ação remove os dados do usuário do sistema de teste. Para confirmar, digite <span className="font-semibold">EXCLUIR USUARIO</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              Você está excluindo <span className="font-semibold">{user?.name}</span> ({user?.email}). O histórico de grupos, mensagens, tickets e cobranças locais também será removido.
            </div>
            <div>
              <label className="text-sm text-zinc-600 block mb-2">Confirmação</label>
              <Input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder="Digite EXCLUIR USUARIO"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deletingUser}>
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleDeleteUser}
              disabled={!deletePhraseIsValid || deletingUser}
            >
              {deletingUser ? 'Excluindo...' : 'Confirmar exclusão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
